
import { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';

export interface ExhibitData {
  id: string;
  numberCode: string;
  status: string;
  title: string;
  subtitle: string;
  description: string;
  images: Array<{url: string; type: string; mimeType?: string; language?: string}>;
  audio: Array<{url: string; type: string; language: string; mimeType?: string}>;
  videos: Array<{url: string; type: string; mimeType?: string; language?: string}>;
  hasAudio: boolean;
  hasVideo: boolean;
  location?: {
    floor: number;
    room: string;
  } | null;
  translations?: {
    [language: string]: {
      title: string;
      subtitle?: string;
      description?: string;
    }
  };
}

export function useExhibitData(exhibitId: string, language: string = 'en') {
  const [exhibit, setExhibit] = useState<ExhibitData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  useEffect(() => {
    if (!exhibitId) return;
    
    const fetchExhibit = async () => {
      try {
        setLoading(true);
        
        // CRITICAL: Only fetch published exhibits for public frontend
        const { data, error } = await supabase
          .from('exhibits')
          .select(`
            id,
            number_code,
            status,
            translations:exhibit_translations(
              language_code,
              title,
              subtitle,
              description
            ),
            media:exhibit_media(
              type,
              sequence_order,
              media_file:media_id(
                id,
                type,
                url,
                mime_type,
                language_code,
                metadata
              )
            )
          `)
          .eq('id', exhibitId)
          .eq('status', 'published') // CRITICAL: Only published exhibits
          .single();
          
        if (error) {
          if (error.code === 'PGRST116') {
            console.warn(`No published exhibit found with ID: ${exhibitId} (may be draft)`);
            throw new Error("Exhibit not found or not published");
          }
          throw error;
        }
        
        if (!data || !data.translations) {
          throw new Error("No published exhibit data found");
        }
        
        console.log(`DEBUG: Fetched exhibit status: ${data.status} (should be 'published')`);
        
        // Process translations
        const currentLangTranslation = data.translations.find(
          t => t.language_code === language
        );
        const englishTranslation = data.translations.find(
          t => t.language_code === 'en'
        );
        const fallbackTranslation = data.translations[0];
        
        const activeTranslation = currentLangTranslation || englishTranslation || fallbackTranslation;
        
        console.log('Available translations:', data.translations);
        console.log('Selected translation:', activeTranslation);
        
        // Process media with language filtering
        const images = data.media
          .filter(m => {
            if (m.type !== 'image' || !m.media_file || !m.media_file.url) return false;
            // Show images for current language OR images with no language specified
            const mediaLanguage = m.media_file.language_code;
            return !mediaLanguage || mediaLanguage === language;
          })
          .sort((a, b) => a.sequence_order - b.sequence_order)
          .map(m => ({
            url: m.media_file.url,
            type: m.media_file.type,
            mimeType: m.media_file.mime_type,
            language: m.media_file.language_code
          }));
          
        const audio = data.media
          .filter(m => m.type === 'audio' && m.media_file && m.media_file.url)
          .sort((a, b) => a.sequence_order - b.sequence_order)
          .map(m => ({
            url: m.media_file.url,
            type: m.media_file.type,
            language: m.media_file.language_code || 'en',
            mimeType: m.media_file.mime_type
          }));
          
        const videos = data.media
          .filter(m => {
            if (m.type !== 'video' || !m.media_file || !m.media_file.url) return false;
            // Show videos for current language OR videos with no language specified
            const mediaLanguage = m.media_file.language_code;
            return !mediaLanguage || mediaLanguage === language;
          })
          .sort((a, b) => a.sequence_order - b.sequence_order)
          .map(m => ({
            url: m.media_file.url,
            type: m.media_file.type,
            mimeType: m.media_file.mime_type,
            language: m.media_file.language_code
          }));
        
        // Look for location data in media metadata with strict validation
        let locationData = null;
        
        // Find first image media item with valid location metadata
        const imageWithLocation = data.media
          .filter(m => m.type === 'image' && m.media_file && m.media_file.metadata)
          .find(m => {
            if (!m.media_file || !m.media_file.metadata) return false;
            
            const metadata = m.media_file.metadata;
            if (typeof metadata !== 'object' || metadata === null) return false;
            
            // Check if location property exists
            return 'location' in metadata;
          });
          
        // Extract location data with thorough validation
        if (imageWithLocation && imageWithLocation.media_file && imageWithLocation.media_file.metadata) {
          const metadata = imageWithLocation.media_file.metadata;
          
          if (typeof metadata === 'object' && metadata !== null && 'location' in metadata) {
            const locationMeta = metadata.location;
            
            // Strict validation of location data format
            if (locationMeta && 
                typeof locationMeta === 'object' &&
                'floor' in locationMeta && 
                'room' in locationMeta &&
                typeof locationMeta.floor === 'number' && 
                typeof locationMeta.room === 'string' && 
                locationMeta.room.trim() !== '') {
              
              locationData = {
                floor: locationMeta.floor,
                room: locationMeta.room
              };
              
              console.log('Found valid location data:', locationData);
            } else {
              console.log('Invalid location data format:', locationMeta);
            }
          }
        }
        
        // Format the exhibit data
        const formattedExhibit = {
          id: data.id,
          numberCode: data.number_code,
          status: data.status,
          title: activeTranslation?.title || `Exhibit ${data.number_code}`,
          subtitle: activeTranslation?.subtitle || '', 
          description: activeTranslation?.description || '',
          images: images.length > 0 ? images : [{ url: '/placeholder.svg', type: 'image' }],
          audio: audio.length > 0 ? audio : [],
          videos: videos.length > 0 ? videos : [],
          hasAudio: audio.length > 0,
          hasVideo: videos.length > 0,
          location: locationData, // Only set if valid
          translations: data.translations.reduce((acc, t) => {
            acc[t.language_code] = {
              title: t.title,
              subtitle: t.subtitle,
              description: t.description
            };
            return acc;
          }, {})
        };
        
        setExhibit(formattedExhibit);
      } catch (err) {
        console.error('Error fetching published exhibit:', err);
        setError(err);
      } finally {
        setLoading(false);
      }
    };
    
    fetchExhibit();
  }, [exhibitId, language]);
  
  return { exhibit, loading, error };
}
